import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../utils/api'
import { auth } from '../lib/firebase'

export default function Login() {
  const navigate = useNavigate()
  const { login, setUser } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  
  const isMockMode = import.meta.env.VITE_USE_MOCK === 'true';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    
    try {
      if (isMockMode || !import.meta.env.VITE_FIREBASE_API_KEY) {
        const mockToken = 'mock-token-' + Date.now()
        
        const response = await api.auth.verify(mockToken)
        
        await login(mockToken)
        setUser({ uid: response.uid, email: response.email })
        localStorage.setItem('beat-auth-token', mockToken)
        
        await new Promise(resolve => setTimeout(resolve, 100))
        
        navigate('/dashboard')
        return
      }
      
      const { GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } = await import('firebase/auth')
      const provider = new GoogleAuthProvider()
      
      let token: string
      if (email && password) {
        const result = await signInWithEmailAndPassword(auth, email, password)
        token = await result.user.getIdToken()
      } else {
        const result = await signInWithPopup(auth, provider)
        token = await result.user.getIdToken()
      }
      
      login(token)
      
      const response = await api.auth.verify(token)
      setUser({ uid: response.uid, email: response.email })
      navigate('/dashboard')
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }
  
  const handleGoogleLogin = async () => {
    setError('')
    setLoading(true)
    
    try {
      const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth')
      const provider = new GoogleAuthProvider()
      
      const result = await signInWithPopup(auth, provider)
      const token = await result.user.getIdToken()
      
      login(token)
      
      const response = await api.auth.verify(token)
      setUser({ uid: response.uid, email: response.email })
      navigate('/dashboard')
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-gray-600">
            Or <a href="/register" className="text-blue-600 hover:underline">create a new account</a>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input mt-1"
                placeholder="you@example.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input mt-1"
                placeholder="••••••••"
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full py-3"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
          
          <div className="text-center">
            <span className="text-gray-500">or</span>
          </div>
          
          <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="btn btn-secondary w-full py-3"
          >
            Continue with Google
          </button>
        </form>
      </div>
    </div>
  )
}